import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToastMessages } from '@/hooks/useToastMessages';

export interface IPhotoUploadState {
  isUploading: boolean;
  uploadError: string | undefined;
}

export interface IPhotoUploadActions {
  uploadPhoto: (imageUri: string | File, uploadFn: (imageUri: string | File) => Promise<void>) => Promise<void>;
  clearError: () => void;
  resetState: () => void;
}

export interface IUsePhotoUploadReturn extends IPhotoUploadState, IPhotoUploadActions {}

/**
 * Hook that manages photo upload state and provides upload functionality
 * 
 * @returns Object with upload state and actions
 */
export function usePhotoUpload(): IUsePhotoUploadReturn {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);

  const uploadPhoto = useCallback(
    async (imageUri: string | File, uploadFn: (imageUri: string | File) => Promise<void>) => {
      setIsUploading(true);
      setUploadError(undefined);
      
      try {
        await uploadFn(imageUri);
        showSuccessToast(t('messages.photoUploadSuccess', 'Photo uploaded successfully'));
      } catch (error) {
        const errorMessage = t('messages.photoUploadFailed', 'Photo upload failed');
        setUploadError(errorMessage);
        showErrorToast(errorMessage, error);
      } finally {
        setIsUploading(false);
      }
    },
    [t, showSuccessToast, showErrorToast]
  );

  const clearError = useCallback(() => {
    setUploadError(undefined);
  }, []);

  const resetState = useCallback(() => {
    setIsUploading(false);
    setUploadError(undefined);
  }, []);

  return {
    isUploading,
    uploadError,
    uploadPhoto,
    clearError,
    resetState,
  };
} 